import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { billingApi } from '../api/billing.ts';
import { useAuthStore } from '../state/authStore.ts';
import { ApiError } from '../api/client.ts';
import { useSeo } from '../hooks/useSeo.ts';
import { getStoredGclid, trackEvent } from '../analytics.ts';
import PlanRecommender, { type PlanCode } from '../components/PlanRecommender.tsx';

/**
 * Pricing page with a Monthly / Annual billing-cycle toggle.
 *
 * Today: only Monthly prices exist (PlanCatalog defines pkg_single_scan, sub_basic, sub_agency).
 * The Annual side of the toggle shows the projected pricing (-20% per ROI standard) but the
 * "Buy" button is disabled with a "Coming soon" note.
 *
 * TO ENABLE ANNUAL BILLING (operator checklist):
 *   1. In Stripe Dashboard, create Annual recurring Prices for the Basic + Agency Products.
 *      Suggested amounts: Basic €86.40/year (€7.20/mo equiv, 20% off), Agency €470.40/year
 *      (€39.20/mo equiv, 20% off).
 *   2. Add PlanCatalog entries: sub_basic_annual, sub_agency_annual.
 *   3. Add their Price IDs to appsettings.Production.json Billing.StripePriceIds.
 *   4. Replace the ANNUAL_PLAN_MAP placeholder below with real annual plan codes.
 *   5. Remove the "annual.disabled" override so the button is enabled when Annual is selected.
 *
 * Conversion lift: ~30-50% of subscribers pick annual when offered, which both improves cash
 * collection upfront AND reduces churn (annual subs renew at ~95% vs monthly ~85%).
 */

type BillingCycle = 'monthly' | 'annual';

// TODO: USER INPUT REQUIRED — replace placeholder annual codes with real plan codes once the
// Stripe Annual prices exist (see checklist in the file header above). Until then, Annual
// rows render with a disabled "Coming soon" button.
const ANNUAL_PLAN_MAP: Record<string, { code: string; available: boolean } | null> = {
  pkg_single_scan: null, // One-time — annual N/A
  sub_basic: { code: 'sub_basic_annual', available: false },
  sub_agency: { code: 'sub_agency_annual', available: false },
};

const ANNUAL_DISCOUNT = 0.20;

export default function PricingPage() {
  useSeo({
    title: 'Pricing — €29 single scan or €9/mo for 5',
    canonicalPath: '/pricing',
    description:
      '€29 one-time scan, €9/mo for 5 scans, €49/mo agency plan with branded reports. EU VAT included automatically. 7-day money-back.',
  });

  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [recommended, setRecommended] = useState<PlanCode | null>(null);
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const { data: plans, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: billingApi.plans,
  });

  const checkout = useMutation({
    mutationFn: (planCode: string) => {
      const planPriceMap: Record<string, number> = {
        pkg_single_scan: 29,
        sub_basic: 9,
        sub_agency: 49,
      };
      trackEvent('begin_checkout', {
        currency: 'EUR',
        value: planPriceMap[planCode] ?? 0,
        items: [{ item_id: planCode, item_name: planCode, quantity: 1 }],
      });
      return billingApi.checkout(planCode, getStoredGclid());
    },
    onSuccess: result => {
      window.location.href = result.url;
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/sign-in?returnUrl=/pricing');
      }
    },
  });

  return (
    <div className="page pricing">
      <h1>Pricing</h1>
      <p className="lead">Pay per scan or subscribe for monthly compliance checks.</p>

      <div className="pricing-anchor" role="note">
        <span className="pricing-anchor-old">Typical consultant audit: €5,000–€10,000</span>
        <span className="pricing-anchor-arrow" aria-hidden="true">→</span>
        <span className="pricing-anchor-new">EAA Proof: from €29</span>
      </div>

      <div className="pricing-reassurance">
        <span className="pricing-reassurance-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
          7-day money-back guarantee
        </span>
        <span className="pricing-reassurance-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
          Cancel anytime, no commitment
        </span>
        <span className="pricing-reassurance-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
          EU VAT auto-applied + B2B reverse-charge
        </span>
      </div>

      <p className="pricing-sample-link">
        <Link to="/sample">See a real sample report →</Link>
        <span className="muted small"> Generated with the same code that handles paid scans.</span>
        {' · '}
        <Link to="/roi-calculator">See EAA penalties by member state →</Link>
      </p>

      <PlanRecommender onRecommendation={setRecommended} />

      <div className="billing-toggle" role="radiogroup" aria-label="Billing cycle">
        <button
          type="button"
          role="radio"
          aria-checked={cycle === 'monthly'}
          className={`billing-toggle-opt${cycle === 'monthly' ? ' is-active' : ''}`}
          onClick={() => setCycle('monthly')}
        >
          Monthly
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={cycle === 'annual'}
          className={`billing-toggle-opt${cycle === 'annual' ? ' is-active' : ''}`}
          onClick={() => setCycle('annual')}
        >
          Annual <span className="billing-toggle-save">save 20%</span>
        </button>
      </div>

      {isLoading || !plans ? (
        <p className="muted">Loading plans…</p>
      ) : (
        <div className="plan-grid">
          {plans.map(plan => {
            // Recommender result takes precedence — when the user has answered the wizard,
            // their recommendation gets the "Recommended for you" badge and the visual lift
            // instead of the static "Most popular" pick. Falls back to Basic-as-default when
            // the user hasn't engaged with the wizard.
            const isRecommended = recommended !== null && plan.code === recommended;
            const isPopular = recommended === null && plan.code === 'sub_basic';
            const highlight = isRecommended || isPopular;
            const isOneTime = plan.kind !== 'Subscription';
            const annualMeta = ANNUAL_PLAN_MAP[plan.code];

            // For one-time plans, annual makes no sense — always show monthly price.
            // For subscriptions, show discounted annual-per-month if cycle === 'annual'.
            const showAnnual = cycle === 'annual' && annualMeta !== null;
            const monthlyEquivalent = showAnnual
              ? plan.priceEur * (1 - ANNUAL_DISCOUNT)
              : plan.priceEur;
            const yearlyTotal = monthlyEquivalent * 12;
            const annualBlocked = showAnnual && annualMeta && !annualMeta.available;

            return (
              <div key={plan.code} className={`plan-card${highlight ? ' plan-popular' : ''}${isRecommended ? ' plan-recommended' : ''}`}>
                {isRecommended ? (
                  <span className="plan-popular-badge plan-recommended-badge">
                    Recommended for you
                  </span>
                ) : isPopular ? (
                  <span className="plan-popular-badge">Most popular</span>
                ) : null}
                <h2>{plan.displayName}</h2>

                {isOneTime ? (
                  <p className="price">
                    €{plan.priceEur.toFixed(0)}<span> one-time</span>
                  </p>
                ) : showAnnual ? (
                  <>
                    <p className="price">
                      €{monthlyEquivalent.toFixed(2)}<span>/mo</span>
                    </p>
                    <p className="muted small price-sub">
                      Billed annually at €{yearlyTotal.toFixed(2)}
                    </p>
                  </>
                ) : (
                  <p className="price">
                    €{plan.priceEur.toFixed(0)}<span>/mo</span>
                  </p>
                )}

                <p className="muted">{plan.creditsGranted} scan{plan.creditsGranted === 1 ? '' : 's'}{!isOneTime ? '/mo' : ''}</p>
                <p>{plan.description}</p>

                {annualBlocked ? (
                  <>
                    <button type="button" className="cta" disabled>
                      Coming soon
                    </button>
                    <p className="muted small price-sub">
                      Annual billing launches Q3 2026. Switch to Monthly above to buy now.
                    </p>
                  </>
                ) : (
                  <button
                    type="button"
                    className="cta"
                    onClick={() => checkout.mutate(plan.code)}
                    disabled={checkout.isPending}
                  >
                    {user ? 'Buy' : 'Sign in to buy'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="pricing-footnotes">
        <p className="muted small">
          EU VAT is added automatically at checkout. B2B reverse-charge supported with a valid
          VAT ID. All plans include a 7-day money-back guarantee — see the{' '}
          <Link to="/refund-policy">refund policy</Link>.
        </p>
      </div>
    </div>
  );
}
